import React, { useState, useEffect } from "react";
import { Save, Loader2, Search, Camera } from "lucide-react";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Modal from "../../components/ui/Modal";
import { AdminService } from "../../../application/services/AdminService";
import { useToast } from "../../../context/ToastContext";

const getLocalDateString = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().split("T")[0];
};

const ConsumoForm = ({ onClose, onSuccess, initialData }) => {
  const toast = useToast();
  const isEditing = Boolean(initialData?.id_consumo);

  // Estado del formulario
  const [formData, setFormData] = useState({
    id_medidor: "",
    id_tarifa: "",
    lectura_anterior: "",
    lectura_actual: "",
    fecha_lectura: getLocalDateString(),
    reconexion: "NO",
  });
  const [fotoEvidencia, setFotoEvidencia] = useState(null);

  // Estados para búsqueda y selección
  const [searchQuery, setSearchQuery] = useState("");
  const [clientsFound, setClientsFound] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientMeters, setClientMeters] = useState([]);
  const [selectedMeter, setSelectedMeter] = useState(null);

  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMeters, setIsLoadingMeters] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Cargar datos iniciales si es edición
  useEffect(() => {
    if (initialData) {
      setFormData({
        id_medidor: initialData.id_medidor || "",
        id_tarifa: initialData.id_tarifa || "",
        lectura_anterior: initialData.lectura_anterior || "",
        lectura_actual: initialData.lectura_actual || "",
        fecha_lectura:
          initialData.fecha_lectura?.split("T")[0] || getLocalDateString(),
        reconexion: initialData.reconexion || "NO",
      });
      // Si hay datos del medidor/cliente, podríamos precargarlos visualmente
      if (initialData.medidor) {
        setSelectedMeter(initialData.medidor);
        if (initialData.cliente) {
          setSelectedClient(initialData.cliente);
        }
      }
      setFotoEvidencia(null);
    }
  }, [initialData]);

  // Buscar clientes por nombre
  const handleSearchClient = async () => {
    if (!searchQuery.trim()) {
      toast.error("Ingrese un nombre para buscar");
      return;
    }

    setIsSearching(true);
    setClientsFound([]);
    setSelectedClient(null);
    setClientMeters([]);
    setSelectedMeter(null);

    try {
      const response = await AdminService.searchClientes(searchQuery);
      const found = response.items || response || [];
      if (found && found.length > 0) {
        setClientsFound(found);
      } else {
        toast.info("No se encontraron clientes con ese nombre");
      }
    } catch (error) {
      console.error("Error searching clients:", error);
      toast.error("Error al buscar clientes");
    } finally {
      setIsSearching(false);
    }
  };

  // Seleccionar cliente y cargar sus medidores
  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setClientsFound([]); // Limpiar lista de busqueda para limpiar vista
    setIsLoadingMeters(true);

    try {
      const response = await AdminService.getMedidoresByCliente(
        client.id_cliente,
      );
      const meters = response.items || response || [];
      setClientMeters(meters);
      if (!meters || meters.length === 0) {
        toast.warning("Este cliente no tiene medidores registrados");
      }
    } catch (error) {
      console.error("Error fetching meters:", error);
      toast.error("Error al cargar medidores del cliente");
    } finally {
      setIsLoadingMeters(false);
    }
  };

  // Seleccionar medidor y cargar información (lectura anterior)
  const handleSelectMeter = async (meter) => {
    setSelectedMeter(meter);
    // Reset form specifics
    setFormData((prev) => ({
      ...prev,
      id_medidor: meter.id_medidor,
      id_tarifa: meter.id_tarifa,
      lectura_anterior: "",
      lectura_actual: "",
    }));

    try {
      const info = await AdminService.getMedidorInfo(meter.codigo_medidor);
      setFormData((prev) => ({
        ...prev,
        lectura_anterior: info.lectura_anterior || 0,
      }));
      setErrors((prev) => ({ ...prev, medidor: null }));
    } catch (error) {
      console.error("Error fetching meter info:", error);
      toast.error("Error al obtener la lectura anterior");
    }
  };

  const resetSearch = () => {
    setSelectedClient(null);
    setSelectedMeter(null);
    setClientMeters([]);
    setClientsFound([]);
    setSearchQuery("");
    setFormData((prev) => ({
      ...prev,
      id_medidor: "",
      lectura_anterior: "",
      lectura_actual: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.id_medidor) {
      newErrors.medidor = "Debe seleccionar un medidor";
    }

    if (!formData.lectura_actual || formData.lectura_actual === "") {
      newErrors.lectura_actual = "La lectura actual es requerida";
    } else if (
      Number(formData.lectura_actual) < Number(formData.lectura_anterior)
    ) {
      newErrors.lectura_actual =
        "La lectura actual no puede ser menor a la anterior";
    }

    if (!formData.fecha_lectura) {
      newErrors.fecha_lectura = "La fecha de lectura es requerida";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Por favor corrija los errores del formulario");
      return;
    }

    setIsLoading(true);

    try {
      // Usar FormData para soportar carga de archivos
      const formDataToSend = new FormData();
      formDataToSend.append("id_medidor", formData.id_medidor);
      formDataToSend.append("id_tarifa", formData.id_tarifa);
      formDataToSend.append(
        "lectura_anterior",
        formData.lectura_anterior === "" ? 0 : Number(formData.lectura_anterior)
      );
      formDataToSend.append("lectura_actual", Number(formData.lectura_actual));
      formDataToSend.append("fecha_lectura", formData.fecha_lectura);
      formDataToSend.append("reconexion", formData.reconexion || "NO");

      if (fotoEvidencia) {
        formDataToSend.append("foto_evidencia", fotoEvidencia);
      }

      if (isEditing) {
        await AdminService.updateConsumo(initialData.id_consumo, formDataToSend);
        toast.success("Consumo actualizado exitosamente");
      } else {
        await AdminService.createConsumo(formDataToSend);
        toast.success("Consumo registrado exitosamente");
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving consumo:", error);
      toast.error(
        typeof error === "string" ? error : "Error al guardar el consumo",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const consumoTotal =
    formData.lectura_actual &&
    Number(formData.lectura_actual) >= Number(formData.lectura_anterior || 0)
      ? Math.max(
          0,
          Number(formData.lectura_actual) -
            Number(formData.lectura_anterior || 0),
        )
      : 0;

  const tarifaBaseLimit = selectedMeter?.tarifa?.tarifa_base
    ? Number(selectedMeter.tarifa.tarifa_base)
    : 0;
  const consumoBase =
    tarifaBaseLimit > 0
      ? Math.min(consumoTotal, tarifaBaseLimit)
      : consumoTotal;
  const consumoExceso =
    tarifaBaseLimit > 0 ? Math.max(0, consumoTotal - tarifaBaseLimit) : 0;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      preventCloseOnOverlayClick={true}
      title={isEditing ? "Editar Consumo" : "Registrar Consumo"}
      size="xl"
      footer={
        <div className="flex w-full justify-between items-center">
          {/* Consumo detallado en footer */}
          <div className="flex gap-4 items-center">
            <div
              className={`text-sm ${consumoBase > 0 ? "text-blue-700 font-bold" : "text-gray-500"}`}
            >
              Consumo Base: <span className="text-xl ml-1">{consumoBase}</span>{" "}
              m³
            </div>
            {selectedMeter?.tarifa?.tarifa_base && (
              <div
                className={`text-sm ${consumoExceso > 0 ? "text-orange-600 font-bold" : "text-gray-400"}`}
              >
                Consumo Exceso:{" "}
                <span className="text-xl ml-1">{consumoExceso}</span> m³
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={!selectedMeter && !isEditing}
            >
              <Save className="w-4 h-4 mr-2" />
              {isEditing ? "Actualizar" : "Registrar"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* 1. SECCIÓN DE BÚSQUEDA Y SELECCIÓN (Solo visible si no es edición) */}
        {!isEditing && (
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                1. Buscar Cliente / Medidor
              </h4>
              {(selectedClient || selectedMeter) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={resetSearch}
                  className="text-red-600 hover:bg-red-50 h-8 text-xs px-3"
                >
                  Limpiar Búsqueda
                </Button>
              )}
            </div>

            {/* Search Input */}
            <div className="flex gap-2 mb-3">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nombre o código (espacio al final para exacto)..."
                className="flex-1 h-10"
                onKeyDown={(e) => e.key === "Enter" && handleSearchClient()}
                disabled={selectedClient !== null}
              />
              <Button
                onClick={handleSearchClient}
                disabled={isSearching || selectedClient !== null}
                className="shrink-0 h-10 px-4"
              >
                {isSearching ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* RESULTADOS DE BÚSQUEDA (CLIENTES) */}
            {clientsFound.length > 0 && !selectedClient && (
              <div className="bg-white border rounded-md shadow-sm max-h-48 overflow-y-auto mb-2">
                {clientsFound.map((client) => (
                  <div
                    key={client.id_cliente}
                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-0 flex justify-between items-center group"
                    onClick={() => handleSelectClient(client)}
                  >
                    <div className="flex items-baseline gap-2">
                      <p className="font-medium text-gray-800">
                        {client.nombres} {client.apellidos}
                      </p>
                      <p className="text-xs text-blue-600 font-semibold bg-blue-50 px-2 py-0.5 rounded border border-blue-100 whitespace-nowrap">
                        Medidor:{" "}
                        {client.medidores && client.medidores.length > 0
                          ? client.medidores.join(", ")
                          : "S/M"}
                      </p>
                    </div>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      Seleccionar
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* SELECCIÓN DE MEDIDOR (Una vez seleccionado el cliente) */}
            {selectedClient && (
              <div className="animate-in fade-in zoom-in duration-300">
                <div className="flex items-center justify-between gap-3 mb-3 p-3 bg-blue-50/50 rounded border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                      {selectedClient.nombres.charAt(0)}
                    </div>
                    <p className="font-medium text-gray-900 leading-none">
                      {selectedClient.nombres} {selectedClient.apellidos}
                    </p>
                  </div>
                  <div className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                    Cliente Seleccionado
                  </div>
                </div>

                {isLoadingMeters ? (
                  <div className="flex justify-center p-4">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {clientMeters.map((meter) => (
                      <div
                        key={meter.id_medidor}
                        onClick={() => handleSelectMeter(meter)}
                        className={`
                            px-4 py-3 rounded-lg border cursor-pointer transition-all flex justify-between items-center
                            ${
                              selectedMeter?.id_medidor === meter.id_medidor
                                ? "bg-blue-600 border-blue-600 text-white shadow-md"
                                : "bg-white border-gray-200 hover:border-blue-400 hover:shadow-sm"
                            }
                            `}
                      >
                        <div className="flex items-center gap-2 overflow-hidden">
                          <span
                            className={`font-bold whitespace-nowrap ${selectedMeter?.id_medidor === meter.id_medidor ? "text-white" : "text-gray-800"}`}
                          >
                            Cod: {meter.codigo_medidor}
                          </span>
                          <span
                            className={`text-sm ${selectedMeter?.id_medidor === meter.id_medidor ? "text-blue-200" : "text-gray-400"}`}
                          >
                            |
                          </span>
                          <p
                            className={`text-sm truncate ${selectedMeter?.id_medidor === meter.id_medidor ? "text-blue-50" : "text-gray-600"}`}
                          >
                            {meter.ubicacion_medidor || "Sin ubicación"}
                          </p>
                        </div>
                        <div
                          className={`ml-2 text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                            selectedMeter?.id_medidor === meter.id_medidor
                              ? "bg-blue-500 text-white"
                              : meter.estado_medidor === "activo"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                          }`}
                        >
                          {meter.estado_medidor}
                        </div>
                      </div>
                    ))}
                    {clientMeters.length === 0 && (
                      <div className="col-span-2 p-3 text-sm text-red-500 bg-red-50 rounded border border-red-100">
                        Este cliente no tiene medidores registrados.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 2. FORMULARIO DE CONSUMO (Siempre visible, pero deshabilitado hasta seleccionar medidor) */}
        <div
          className={`transition-opacity duration-300 ${!selectedMeter && !isEditing ? "opacity-50 grayscale-[0.5] pointer-events-none" : "opacity-100"}`}
        >
          <div className="flex items-center justify-between mb-3 border-b pb-2">
            <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
              2. Detalles del Consumo
            </h4>
            {selectedMeter && (
              <div className="text-sm text-blue-700 bg-blue-50 px-3 py-1 rounded-md">
                <span className="font-semibold">
                  Medidor: {selectedMeter.codigo_medidor}
                </span>
                <span className="mx-2">|</span>
                <span>
                  Anterior: <strong>{formData.lectura_anterior || 0}</strong>
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fecha de lectura */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Fecha de Lectura <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={formData.fecha_lectura}
                onChange={(e) => handleChange("fecha_lectura", e.target.value)}
                error={errors.fecha_lectura}
                disabled={!selectedMeter && !isEditing}
                className=""
              />
            </div>

            {/* Reconexión */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Reconexión
              </label>
              <select
                value={formData.reconexion}
                onChange={(e) => handleChange("reconexion", e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm h-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                disabled={!selectedMeter && !isEditing}
              >
                <option value="NO">NO</option>
                <option value="SI">SI</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {/* Lectura anterior */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Lectura Anterior
              </label>
              <Input
                type="number"
                value={formData.lectura_anterior}
                onChange={(e) =>
                  handleChange("lectura_anterior", e.target.value)
                }
                className="font-mono text-right h-10"
                placeholder="0"
              />
            </div>

            {/* Lectura actual */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Lectura Actual <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                value={formData.lectura_actual}
                onChange={(e) => handleChange("lectura_actual", e.target.value)}
                placeholder="0"
                error={errors.lectura_actual}
                autoFocus={selectedMeter !== null}
                className="text-right font-mono text-lg font-bold"
                disabled={!selectedMeter && !isEditing}
              />
              <div className="flex items-center justify-end mt-1.5">
                <label htmlFor="file-upload" className={`cursor-pointer inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${!selectedMeter && !isEditing ? 'text-gray-400' : 'text-blue-600 hover:text-blue-800'}`}>
                  <Camera className="w-3.5 h-3.5" />
                  {fotoEvidencia ? fotoEvidencia.name : "Adjuntar evidencia (Opcional)"}
                </label>
                <input 
                  id="file-upload" 
                  type="file" 
                  className="hidden" 
                  accept="image/jpeg, image/png, image/gif, image/webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const validExtensions = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
                      if (!validExtensions.includes(file.type)) {
                        toast.error("Formato de imagen no válido. Solo se permiten .jpg, .jpeg, .png, .gif, .webp");
                        e.target.value = null; // reset input
                        return;
                      }
                      setFotoEvidencia(file);
                    }
                  }}
                  disabled={!selectedMeter && !isEditing}
                />
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </Modal>
  );
};

export default ConsumoForm;
